import React, { useEffect, useRef } from "react";
import { animate, stagger } from "animejs";

/**
 * Animated number that counts up from 0 to `value` using anime.js.
 * `format` controls how the running value is rendered.
 */
export const CountUp: React.FC<{
  value: number;
  format?: (n: number) => string;
  duration?: number;
  className?: string;
  style?: React.CSSProperties;
}> = ({
  value,
  format = (n) => String(Math.round(n)),
  duration = 1100,
  className,
  style,
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const fmtRef = useRef(format);
  fmtRef.current = format;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obj = { v: 0 };
    const ctrl = animate(obj, {
      v: value,
      duration,
      ease: "outExpo",
      onUpdate: () => {
        node.textContent = fmtRef.current(obj.v);
      },
    });
    return () => {
      try {
        (ctrl as unknown as { pause?: () => void }).pause?.();
      } catch {
        /* noop */
      }
    };
  }, [value, duration]);

  return (
    <span ref={ref} className={className} style={style}>
      {format(0)}
    </span>
  );
};

/**
 * Entrance animation for a group of children. Attach the returned ref to a
 * container; its direct element children matching `selector` fade/rise in with
 * a stagger. Re-runs whenever `trigger` changes.
 */
export function useStaggerIn<T extends HTMLElement>(
  selector: string,
  trigger: unknown,
  opts?: { y?: number; delay?: number; duration?: number; stagger?: number },
) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const els = root.querySelectorAll(selector);
    if (els.length === 0) return;
    const ctrl = animate(els, {
      opacity: [0, 1],
      translateY: [opts?.y ?? 14, 0],
      duration: opts?.duration ?? 520,
      delay: stagger(opts?.stagger ?? 60, { start: opts?.delay ?? 0 }),
      ease: "outExpo",
    });
    return () => {
      try {
        (ctrl as unknown as { pause?: () => void }).pause?.();
      } catch {
        /* noop */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);
  return ref;
}

/** Animate an SVG ring's stroke-dashoffset from empty to its target. */
export function useRingSweep(
  pct: number,
  circumference: number,
  trigger: unknown,
) {
  const ref = useRef<SVGCircleElement>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const target = circumference * (1 - Math.max(0, Math.min(1, pct)));
    const obj = { o: circumference };
    node.style.strokeDasharray = String(circumference);
    node.style.strokeDashoffset = String(circumference);
    const ctrl = animate(obj, {
      o: target,
      duration: 1200,
      ease: "outExpo",
      onUpdate: () => {
        node.style.strokeDashoffset = String(obj.o);
      },
    });
    return () => {
      try {
        (ctrl as unknown as { pause?: () => void }).pause?.();
      } catch {
        /* noop */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pct, circumference, trigger]);
  return ref;
}
